import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import PieChart from '../components/PieChart';
import BarChart from '../components/BarChart';
import VerticalBarChart from '../components/VerticalBarChart';
import { formatCurrency } from '../utils/formatCurrency';
import { useVatMode } from '../hooks/useVatMode';
import LoadingSpinner from '../components/LoadingSpinner';
import ErrorMessage from '../components/ErrorMessage';
import CollapsibleSection from '../components/CollapsibleSection';
import ChartTimeRange, { getChartInitialRange } from '../components/ChartTimeRange';
import { apiFetch } from '../utils/api';
import { STATUS_COLORS_HEX as STATUS_COLORS, STATUS_COLORS as STATUS_COLORS_TAILWIND, SUPPLIER_COLORS, SUB_DEPT_COLORS, STATUS_LABELS } from '../constants/colors';
import { UserName } from '../components/InactiveBadge';
import EmptyState from '../components/EmptyState';

// The reverse of STATUS_LABELS, derived rather than hand-written — a future
// relabel (see STATUS_LABELS in constants/colors.js) moves this with it
// instead of needing a second, separately-maintained edit.
const STATUS_LABEL_TO_KEY = Object.fromEntries(
  Object.entries(STATUS_LABELS).map(([key, label]) => [label, key])
);

const DEFAULT_WIDGETS = {
  summaryCards: true,
  statusPieChart: true,
  subDeptSpendChart: true,
  spendOverTime: true,
  statusBreakdown: true,
  supplierSpendChart: true,
  supplierSpendTable: true,
  userSpendChart: true,
  userSpendTable: true,
};

const WIDGET_LABELS = {
  summaryCards: 'Summary Cards',
  statusPieChart: 'Order Status Chart',
  subDeptSpendChart: 'Sub Department Spend Chart',
  spendOverTime: 'Spend Over Time',
  statusBreakdown: 'Status Breakdown',
  supplierSpendChart: 'Supplier Spend Chart',
  supplierSpendTable: 'Supplier Spend Table',
  userSpendChart: 'User Spend Chart',
  userSpendTable: 'User Spend Table',
};

const LS_KEY_WIDGETS = 'sfx_admin_dashboard_widgets';

// Helper to build query string from date range
function buildQS(range) {
  const params = new URLSearchParams();
  if (range && range.start_date) params.set('start_date', range.start_date);
  if (range && range.end_date) params.set('end_date', range.end_date);
  const qs = params.toString();
  return qs ? '?' + qs : '';
}

function AdminDashboard() {
  const navigate = useNavigate();
  const { formatMoney } = useVatMode();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const hasLoadedOnce = useRef(false);
  const [error, setError] = useState(null);
  const [sortField, setSortField] = useState('total_spend');
  const [sortDir, setSortDir] = useState('desc');
  const [supplierSpend, setSupplierSpend] = useState([]);
  const [supplierSortField, setSupplierSortField] = useState('total_spend');
  const [supplierSortDir, setSupplierSortDir] = useState('desc');
  const [spendOverTime, setSpendOverTime] = useState([]);
  const [subDeptSpend, setSubDeptSpend] = useState([]);
  const [spendPeriod, setSpendPeriod] = useState(() => {
    try { return localStorage.getItem('sfx_admin_spend_period') || 'week'; } catch { return 'week'; }
  });
  const [showCustomize, setShowCustomize] = useState(false);
  const [widgets, setWidgets] = useState(() => {
    try {
      const saved = localStorage.getItem(LS_KEY_WIDGETS);
      return saved ? { ...DEFAULT_WIDGETS, ...JSON.parse(saved) } : DEFAULT_WIDGETS;
    } catch {
      return DEFAULT_WIDGETS;
    }
  });

  // Per-chart date ranges (override global when set)
  const [statusPieRange, setStatusPieRange] = useState(() => getChartInitialRange('admin_status_pie'));
  const [supplierChartRange, setSupplierChartRange] = useState(() => getChartInitialRange('admin_supplier_chart'));
  const [subDeptRange, setSubDeptRange] = useState(() => getChartInitialRange('admin_sub_dept'));

  const [userChartRange, setUserChartRange] = useState(() => getChartInitialRange('admin_user_chart'));

  const [spendOTRange, setSpendOTRange] = useState(() => getChartInitialRange('admin_spend_ot'));

  // Per-chart data states for independent fetching
  const [statusPieData, setStatusPieData] = useState(null);
  const [supplierChartData, setSupplierChartData] = useState(null);
  const [subDeptChartData, setSubDeptChartData] = useState(null);

  const [userChartData, setUserChartData] = useState(null);

  // Per-chart RANGED-fetch error flags (F6, silent-failure survey). A ranged
  // fetch failure must NEVER be answered by leaving the previous range's data
  // sitting in state under the new range's label — that is money attributed to
  // the wrong period and it looks entirely healthy. Each handler below sets its
  // flag on failure and clears it on success; the render gates on `!error`,
  // never on the data array, so a rejected/refused request always replaces the
  // chart with an explicit ErrorMessage instead of stale or falsely-empty bars.
  const [statusPieError, setStatusPieError] = useState(false);
  const [supplierChartError, setSupplierChartError] = useState(false);
  const [subDeptChartError, setSubDeptChartError] = useState(false);
  const [userChartError, setUserChartError] = useState(false);
  const [spendOverTimeError, setSpendOverTimeError] = useState(false);

  const fetchSpendOverTime = useCallback((range, period) => {
    const p = period || spendPeriod;
    const params = new URLSearchParams();
    if (range && range.start_date) params.set('start_date', range.start_date);
    if (range && range.end_date) params.set('end_date', range.end_date);
    params.set('period', p);
    setSpendOverTimeError(false);
    apiFetch('/api/dashboard/admin/spend-over-time?' + params.toString())
      .then(res => {
        if (!res.ok) throw new Error('Failed to load spend-over-time data');
        return res.json();
      })
      .then(d => { setSpendOverTime(d.data || []); })
      .catch(() => { setSpendOverTime([]); setSpendOverTimeError(true); });
  }, [spendPeriod]);

  const fetchDashboard = useCallback(() => {
    // Summary cards show all-time figures (no date range filtering)
    if (!hasLoadedOnce.current) setLoading(true);
    setError(null);
    apiFetch('/api/dashboard/admin')
      .then(res => {
        if (!res.ok) throw new Error('Failed to load dashboard data');
        return res.json();
      })
      .then(d => { setData(d); setLoading(false); hasLoadedOnce.current = true; })
      .catch(err => { setError(err.message); setLoading(false); });

    apiFetch('/api/dashboard/admin/supplier-spend')
      .then(res => res.ok ? res.json() : [])
      .then(d => setSupplierSpend(d))
      .catch(() => setSupplierSpend([]));

    apiFetch('/api/dashboard/admin/sub-department-spend')
      .then(res => res.ok ? res.json() : [])
      .then(d => setSubDeptSpend(d))
      .catch(() => setSubDeptSpend([]));

    fetchSpendOverTime();
  }, [fetchSpendOverTime]);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  const handleSpendPeriodChange = (period) => {
    setSpendPeriod(period);
    try { localStorage.setItem('sfx_admin_spend_period', period); } catch {}
    fetchSpendOverTime(spendOTRange, period);
  };

  // Per-chart time range handlers - fetch data independently
  const handleStatusPieRangeChange = (range) => {
    setStatusPieRange(range);
    setStatusPieError(false);
    apiFetch('/api/dashboard/admin' + buildQS(range))
      .then(res => {
        if (!res.ok) throw new Error('Failed to load status breakdown');
        return res.json();
      })
      .then(d => { setStatusPieData(d.status_breakdown); })
      .catch(() => { setStatusPieError(true); });
  };

  const handleSupplierChartRangeChange = (range) => {
    setSupplierChartRange(range);
    setSupplierChartError(false);
    apiFetch('/api/dashboard/admin/supplier-spend' + buildQS(range))
      .then(res => {
        if (!res.ok) throw new Error('Failed to load supplier spend');
        return res.json();
      })
      .then(d => setSupplierChartData(d))
      .catch(() => { setSupplierChartError(true); });
  };

  const handleSubDeptRangeChange = (range) => {
    setSubDeptRange(range);
    setSubDeptChartError(false);
    apiFetch('/api/dashboard/admin/sub-department-spend' + buildQS(range))
      .then(res => {
        if (!res.ok) throw new Error('Failed to load sub-department spend');
        return res.json();
      })
      .then(d => setSubDeptChartData(d))
      .catch(() => { setSubDeptChartError(true); });
  };


  const handleUserChartRangeChange = (range) => {
    setUserChartRange(range);
    setUserChartError(false);
    apiFetch('/api/dashboard/admin' + buildQS(range))
      .then(res => {
        if (!res.ok) throw new Error('Failed to load user spend');
        return res.json();
      })
      .then(d => { setUserChartData(d.user_summaries); })
      .catch(() => { setUserChartError(true); });
  };

  // formatCurrency imported from utils/formatCurrency

  const getStatusColor = (status) => {
    return STATUS_COLORS_TAILWIND[status] || 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
  };

  // Derived from the shared `STATUS_LABELS` map (constants/colors.js) so a
  // future relabel — the last one was "Partially Delivered" → "Partial
  // delivery" — moves this page without a second, hand-written edit. Falls
  // back to a capitalised raw status for anything `STATUS_LABELS` does not
  // carry (there is currently no such status).
  const getStatusLabel = (status) => {
    return STATUS_LABELS[status] || (status.charAt(0).toUpperCase() + status.slice(1));
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir(field === 'username' ? 'asc' : 'desc');
    }
  };

  const getSortedUsers = () => {
    if (!data || !data.user_summaries) return [];
    return [...data.user_summaries].sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];
      if (sortField === 'username') {
        aVal = (aVal || '').toLowerCase();
        bVal = (bVal || '').toLowerCase();
      }
      if (aVal < bVal) return sortDir === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
  };

  const handleSupplierSort = (field) => {
    if (supplierSortField === field) {
      setSupplierSortDir(supplierSortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSupplierSortField(field);
      setSupplierSortDir(field === 'supplier_name' ? 'asc' : 'desc');
    }
  };

  const getSortedSuppliers = () => {
    return [...supplierSpend].sort((a, b) => {
      let aVal = a[supplierSortField];
      let bVal = b[supplierSortField];
      if (supplierSortField === 'supplier_name') {
        aVal = (aVal || '').toLowerCase();
        bVal = (bVal || '').toLowerCase();
      }
      if (aVal < bVal) return supplierSortDir === 'asc' ? -1 : 1;
      if (aVal > bVal) return supplierSortDir === 'asc' ? 1 : -1;
      return 0;
    });
  };

  const SortIcon = ({ field, activeField, activeDir }) => {
    const sf = activeField || sortField;
    const sd = activeDir || sortDir;
    if (sf !== field) return <span className="text-gray-400 dark:text-gray-500 ml-1">{'\u2195'}</span>;
    return <span className="ml-1">{sd === 'asc' ? '\u2191' : '\u2193'}</span>;
  };

  const toggleWidget = (key) => {
    const updated = { ...widgets, [key]: !widgets[key] };
    setWidgets(updated);
    localStorage.setItem(LS_KEY_WIDGETS, JSON.stringify(updated));
  };

  const resetWidgets = () => {
    setWidgets(DEFAULT_WIDGETS);
    localStorage.setItem(LS_KEY_WIDGETS, JSON.stringify(DEFAULT_WIDGETS));
  };

  // Chart data getters - use per-chart data if available, fall back to global
  const getStatusPieData = () => {
    const breakdown = statusPieData || (data && data.status_breakdown) || [];
    return breakdown.map(s => ({
      label: getStatusLabel(s.status),
      value: s.count,
      color: STATUS_COLORS[s.status] || '#9CA3AF'
    }));
  };

  const getSupplierBarData = () => {
    const source = supplierChartData || supplierSpend;
    const sorted = [...source].sort((a, b) => b.total_spend - a.total_spend);
    return sorted.map((s, i) => ({
      label: s.supplier_name,
      value: s.total_spend,
      color: SUPPLIER_COLORS[i % SUPPLIER_COLORS.length]
    }));
  };

  const getSpendOverTimeData = () => {
    const source = spendOverTime;
    if (!source || source.length === 0) return [];
    const result = source.map((row, index) => {
      let label = row.period_key;
      if (spendPeriod === 'week' && row.period_start) {
        const d = new Date(row.period_start + 'T00:00:00');
        if (!isNaN(d.getTime())) {
          label = d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
        }
      } else if (spendPeriod === 'month' && row.period_key) {
        const parts = row.period_key.split('-');
        if (parts.length === 2) {
          const d = new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, 1);
          if (!isNaN(d.getTime())) {
            label = d.toLocaleDateString('en-GB', { month: 'short', year: 'numeric' });
          }
        }
      }
      return {
        label,
        value: Number(row.total_spend) || 0,
        fullLabel: row.period_key,
        orderCount: row.order_count,
        isCurrent: index === source.length - 1
      };
    });
    return result;
  };

  const getSubDeptPieData = () => {
    const source = subDeptChartData || subDeptSpend;
    if (!source || source.length === 0) return [];
    return source.map((s, i) => ({
      label: s.sub_department,
      value: Number(s.total_spend) || 0,
      color: SUB_DEPT_COLORS[i % SUB_DEPT_COLORS.length]
    }));
  };

  const getUserBarData = () => {
    const source = userChartData || (data && data.user_summaries) || [];
    const sorted = [...source].sort((a, b) => b.total_spend - a.total_spend);
    return sorted.map((u, i) => ({
      label: u.username,
      value: u.total_spend,
      color: SUPPLIER_COLORS[i % SUPPLIER_COLORS.length]
    }));
  };

  if (loading) {
    return (
      <div className="max-w-full mx-auto p-4 lg:p-6 lg:px-8">
        <div className="bg-white rounded-lg shadow-md p-6">
          <LoadingSpinner message="Loading dashboard..." />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-full mx-auto p-4 lg:p-6 lg:px-8">
        <ErrorMessage error={error} onRetry={fetchDashboard} title="Failed to load dashboard" />
      </div>
    );
  }

  // Hero empty state — when there's literally no order activity at all,
  // skip the stats/charts/tables (they'd render as blanks) and show a single
  // intentional invitation. Common for a freshly-set-up production.
  const hasNoData = data && (data.total_orders === 0 || !data.total_orders) &&
    (!data.user_summaries || data.user_summaries.length === 0);
  if (hasNoData) {
    return (
      <div className="max-w-full mx-auto p-4 lg:p-6 lg:px-8">
        <EmptyState
          icon={
            <svg xmlns="http://www.w3.org/2000/svg" className="h-14 w-14 mx-auto text-gray-300 dark:text-gray-600 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
          }
          title="No order activity yet"
          description="Once team members start submitting orders, your spend totals, supplier breakdowns, and per-user summaries will appear here."
          cta={{ label: 'View orders', to: '/admin/orders' }}
        />
      </div>
    );
  }

  const sortedUsers = getSortedUsers();

  return (
    <div className="max-w-full mx-auto p-2.5">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-6">
        <div className="flex items-center gap-2">
          <button
            onClick={() => window.print()}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-blue-50 hover:bg-blue-100 text-blue-700 rounded-lg transition-colors print-hide"
            title="Print dashboard report"
            data-testid="print-dashboard-btn"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
            </svg>
            Print
          </button>
          <button
            onClick={() => setShowCustomize(!showCustomize)}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-gray-100 hover:bg-gray-200 text-gray-600 rounded-lg transition-colors print-hide"
            title="Customize dashboard"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            Customize
          </button>
        </div>
      </div>

      {/* Print-only header */}
      <div className="print-only-header hidden">
        <h1 style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '4px' }}>Admin Dashboard Report</h1>
        <p style={{ fontSize: '12px', color: '#666', marginBottom: '2px' }}>
          Printed: {new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
        </p>
        <hr style={{ margin: '8px 0', borderColor: '#ddd' }} />
      </div>

      {/* Customize Panel */}
      {showCustomize && (
        <div className="bg-white rounded-lg shadow-md p-4 mb-6 border border-blue-200 print-hide">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-gray-700">Customize Dashboard Widgets</h3>
            <button onClick={resetWidgets} className="text-xs text-blue-600 hover:text-blue-800">
              Reset to Default
            </button>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {Object.entries(WIDGET_LABELS).map(([key, label]) => (
              <label key={key} className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={widgets[key]}
                  onChange={() => toggleWidget(key)}
                  className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm text-gray-600">{label}</span>
              </label>
            ))}
          </div>
        </div>
      )}

      {/* Summary Cards */}
      {widgets.summaryCards && (
        <CollapsibleSection id="admin-summary-cards" title="Summary" className="mb-6">
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-2 md:gap-4 p-3 md:p-4">
            <div
              className="bg-gray-50 rounded-lg p-3 md:p-4 cursor-pointer hover:bg-blue-50 hover:ring-2 hover:ring-blue-300 transition-all"
              onClick={() => navigate('/admin/orders')}
              title="View all orders"
              data-testid="total-orders-card"
            >
              <p className="text-xs md:text-sm text-gray-500">Total Orders</p>
              <p className="text-2xl font-bold text-gray-800">{data.total_orders}</p>
              <p className="hidden md:block text-xs text-blue-400 mt-1">Click to view</p>
            </div>
            <div
              className="bg-gray-50 rounded-lg p-3 md:p-4 cursor-pointer hover:bg-green-50 hover:ring-2 hover:ring-green-300 transition-all"
              onClick={() => navigate('/admin/orders')}
              title="View all orders"
              data-testid="total-spend-card"
            >
              <p className="text-xs md:text-sm text-gray-500">Total Spend</p>
              <p className="text-sm md:text-2xl font-bold text-green-600 truncate" title={formatMoney(data.total_spend)}>{formatMoney(data.total_spend)}</p>
              <p className="hidden md:block text-xs text-green-400 mt-1">Click to view orders</p>
            </div>
            <div
              className="bg-gray-50 rounded-lg p-3 md:p-4 cursor-pointer hover:bg-purple-50 hover:ring-2 hover:ring-purple-300 transition-all"
              onClick={() => navigate('/admin/settings#users')}
              title="Manage users"
              data-testid="total-users-card"
            >
              <p className="text-xs md:text-sm text-gray-500">Total Users</p>
              <p className="text-2xl font-bold text-gray-800">{data.user_summaries.length}</p>
              <p className="hidden md:block text-xs text-purple-400 mt-1">Click to manage</p>
            </div>
            <div
              className="bg-gray-50 rounded-lg p-3 md:p-4 cursor-pointer hover:bg-blue-50 hover:ring-2 hover:ring-blue-300 transition-all"
              onClick={() => {
                const el = document.getElementById('admin-user-chart');
                if (el) el.scrollIntoView({ behavior: 'smooth' });
              }}
              title="View per-user spend breakdown"
              data-testid="avg-spend-card"
            >
              <p className="text-xs md:text-sm text-gray-500">Avg Spend/User</p>
              <p className="text-sm md:text-2xl font-bold text-gray-800 truncate">
                {data.user_summaries.length > 0
                  ? formatMoney(data.total_spend / data.user_summaries.length)
                  : '\u00A30.00'}
              </p>
              <p className="hidden md:block text-xs text-blue-400 mt-1">Click for breakdown</p>
            </div>
            <div
              className={`rounded-lg p-3 md:p-4 cursor-pointer hover:ring-2 transition-all ${data.overdue_count > 0 ? 'bg-red-50 border border-red-200 hover:ring-red-300' : 'bg-gray-50 hover:bg-red-50 hover:ring-red-300'}`}
              onClick={() => navigate('/admin/orders?overdue=true')}
              title="View overdue orders"
              data-testid="overdue-orders-card"
            >
              <p className="text-xs md:text-sm text-gray-500">Overdue Orders</p>
              <p className={`text-2xl font-bold ${data.overdue_count > 0 ? 'text-red-600' : 'text-gray-800'}`} data-testid="overdue-count">
                {data.overdue_count || 0}
              </p>
              <p className={`hidden md:block text-xs mt-1 ${data.overdue_count > 0 ? 'text-red-600 dark:text-red-400' : 'text-gray-500 dark:text-gray-400'}`}>Click to view</p>
            </div>
          </div>
        </CollapsibleSection>
      )}

      {/* Charts Row - Pie + Bar side by side on desktop */}
      {(widgets.statusPieChart || widgets.supplierSpendChart) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6 items-start">
          {/* Order Status Pie Chart */}
          {widgets.statusPieChart && (
            <CollapsibleSection id="admin-status-pie" title="Order Status Distribution">
              <div className="p-5">
                <ChartTimeRange chartId="admin_status_pie" onChange={handleStatusPieRangeChange} className="mb-3" />
                {statusPieError ? (
                  <ErrorMessage error="Failed to load order status data for the selected range" onRetry={() => handleStatusPieRangeChange(statusPieRange)} />
                ) : (
                  <PieChart
                    data={getStatusPieData()}
                    size={220}
                    onSliceClick={(slice) => {
                      // The reverse of `getStatusLabel` above, via the module-level
                      // `STATUS_LABEL_TO_KEY` derived from the same `STATUS_LABELS`
                      // map — so the two can never move apart. The fallback is
                      // `slice.label.toLowerCase()`, which would silently navigate
                      // to `?status=partial delivery` — a status no row has —
                      // rather than failing visibly, for any label not in the map.
                      const status = STATUS_LABEL_TO_KEY[slice.label] || slice.label.toLowerCase();
                      navigate(`/admin/orders?status=${encodeURIComponent(status)}`);
                    }}
                  />
                )}
              </div>
            </CollapsibleSection>
          )}

          {/* Supplier Spend Bar Chart */}
          {widgets.supplierSpendChart && (
            <CollapsibleSection id="admin-supplier-chart" title="Spend by Supplier">
              <div className="p-5">
                <ChartTimeRange chartId="admin_supplier_chart" onChange={handleSupplierChartRangeChange} className="mb-3" />
                {supplierChartError ? (
                  <ErrorMessage error="Failed to load supplier spend for the selected range" onRetry={() => handleSupplierChartRangeChange(supplierChartRange)} />
                ) : (
                  <BarChart
                    data={getSupplierBarData()}
                    valuePrefix={'\u00A3'}
                    maxBars={8}
                    onBarClick={(item) => navigate(`/admin/orders?supplier=${encodeURIComponent(item.label)}`)}
                  />
                )}
              </div>
            </CollapsibleSection>
          )}
        </div>
      )}

      {/* Spend per Sub Department Pie Chart */}
      {widgets.subDeptSpendChart && (
        <CollapsibleSection id="admin-sub-dept-spend" title="Spend per Sub Department" className="mb-6" data-testid="sub-dept-spend-chart">
          <div className="p-5">
            <ChartTimeRange chartId="admin_sub_dept" onChange={handleSubDeptRangeChange} className="mb-3" />
            {subDeptChartError ? (
              <ErrorMessage error="Failed to load sub-department spend for the selected range" onRetry={() => handleSubDeptRangeChange(subDeptRange)} />
            ) : (
              <PieChart
                data={getSubDeptPieData()}
                size={240}
                valueFormatter={formatCurrency}
              />
            )}
          </div>
        </CollapsibleSection>
      )}

      {/* Spend Over Time + User Spend - side by side on print */}
      <div className="print-charts-row">
        {/* Spend Over Time Chart */}
        {widgets.spendOverTime && (
          <CollapsibleSection id="admin-spend-over-time" title="Spend Over Time" className="mb-6">
            <div className="p-5">
              <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="spend-period-toggle">
                <span className="text-sm text-gray-500 mr-1">View by:</span>
                <button
                  onClick={() => handleSpendPeriodChange('week')}
                  className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                    spendPeriod === 'week'
                      ? 'bg-blue-600 dark:bg-blue-500/70 text-white shadow-sm'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600'
                  }`}
                  data-testid="spend-period-week"
                >
                  Week
                </button>
                <button
                  onClick={() => handleSpendPeriodChange('month')}
                  className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                    spendPeriod === 'month'
                      ? 'bg-blue-600 dark:bg-blue-500/70 text-white shadow-sm'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600'
                  }`}
                  data-testid="spend-period-month"
                >
                  Month
                </button>
              </div>
              {spendOverTimeError ? (
                <ErrorMessage error="Failed to load spend over time" onRetry={() => fetchSpendOverTime(null, spendPeriod)} />
              ) : (
                <VerticalBarChart
                  data={getSpendOverTimeData()}
                  valuePrefix={'\u00A3'}
                  color="#3B82F6"
                  height={280}
                />
              )}
            </div>
          </CollapsibleSection>
        )}

        {/* User Spend Bar Chart */}
        {widgets.userSpendChart && (
          <CollapsibleSection id="admin-user-chart" title="Spend by User" className="mb-6">
            <div className="p-5">
              <ChartTimeRange chartId="admin_user_chart" onChange={handleUserChartRangeChange} className="mb-3" />
              {userChartError ? (
                <ErrorMessage error="Failed to load user spend for the selected range" onRetry={() => handleUserChartRangeChange(userChartRange)} />
              ) : (
                <BarChart
                  data={getUserBarData()}
                  valuePrefix={'\u00A3'}
                  maxBars={10}
                  onBarClick={(item) => navigate(`/admin/orders?user=${encodeURIComponent(item.label)}`)}
                />
              )}
            </div>
          </CollapsibleSection>
        )}
      </div>

      {/* Status Breakdown */}
      {widgets.statusBreakdown && (
        <CollapsibleSection id="admin-status-breakdown" title="Order Status Breakdown" className="mb-6">
          <div className="flex flex-wrap gap-3 p-4">
            {data.status_breakdown.map(s => (
              <button
                key={s.status}
                onClick={() => navigate(`/admin/orders?status=${encodeURIComponent(s.status)}`)}
                className={`px-3 py-1 rounded-full text-sm font-medium cursor-pointer hover:opacity-80 hover:ring-2 hover:ring-offset-1 hover:ring-gray-400 transition ${getStatusColor(s.status)}`}
                title={`View ${s.status} orders`}
              >
                {getStatusLabel(s.status)}: {s.count}
              </button>
            ))}
          </div>
        </CollapsibleSection>
      )}

      {/* Spend Per Supplier + Per-User Tables - side by side on print */}
      <div className="print-tables-row">
        {/* Spend Per Supplier Table */}
        {widgets.supplierSpendTable && (
          <CollapsibleSection id="admin-supplier-table" title="Spend Per Supplier" className="mb-6">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th
                      className="px-2 sm:px-4 py-3 text-left font-medium text-gray-600 cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSupplierSort('supplier_name')}
                    >
                      Supplier <SortIcon field="supplier_name" activeField={supplierSortField} activeDir={supplierSortDir} />
                    </th>
                    <th
                      className="px-2 sm:px-4 py-3 text-right font-medium text-gray-600 cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSupplierSort('order_count')}
                    >
                      Orders <SortIcon field="order_count" activeField={supplierSortField} activeDir={supplierSortDir} />
                    </th>
                    <th
                      className="px-2 sm:px-4 py-3 text-right font-medium text-gray-600 cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSupplierSort('total_spend')}
                    >
                      Total Spend <SortIcon field="total_spend" activeField={supplierSortField} activeDir={supplierSortDir} />
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {getSortedSuppliers().map((supplier, idx) => (
                    <tr key={supplier.supplier_name} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                      <td className="px-2 sm:px-4 py-3 font-medium max-w-[110px] md:max-w-none">
                        <button
                          onClick={() => navigate(`/admin/orders?supplier=${encodeURIComponent(supplier.supplier_name)}`)}
                          className="text-blue-700 hover:text-blue-900 hover:underline cursor-pointer font-medium block max-w-full truncate text-left md:inline md:max-w-none md:overflow-visible md:whitespace-normal"
                          title={`View orders from ${supplier.supplier_name}`}
                        >
                          {supplier.supplier_name}
                        </button>
                      </td>
                      <td className="px-2 sm:px-4 py-3 text-right text-gray-600">{supplier.order_count}</td>
                      <td className="px-2 sm:px-4 py-3 text-right font-medium text-gray-800 whitespace-nowrap">{formatMoney(supplier.total_spend)}</td>
                    </tr>
                  ))}
                  {supplierSpend.length === 0 && (
                    <tr>
                      <td colSpan="3" className="px-4 py-6 text-center text-gray-500">No supplier spend data yet</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </CollapsibleSection>
        )}

        {/* Per-User Spend Summaries Table */}
        {widgets.userSpendTable && (
          <CollapsibleSection id="admin-user-table" title="Per-User Spend Summary">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th
                      className="px-2 sm:px-4 py-3 text-left font-medium text-gray-600 cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('username')}
                    >
                      User <SortIcon field="username" />
                    </th>
                    <th
                      className="px-2 sm:px-4 py-3 text-right font-medium text-gray-600 cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('order_count')}
                    >
                      Orders <SortIcon field="order_count" />
                    </th>
                    <th
                      className="px-2 sm:px-4 py-3 text-right font-medium text-gray-600 cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('total_spend')}
                    >
                      Total Spend <SortIcon field="total_spend" />
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {sortedUsers.map((user, idx) => (
                    <tr key={user.username} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                      <td className="px-2 sm:px-4 py-3 font-medium max-w-[110px] md:max-w-none">
                        <button
                          onClick={() => navigate(`/admin/orders?user=${encodeURIComponent(user.username)}`)}
                          className="text-blue-700 hover:text-blue-900 hover:underline cursor-pointer font-medium block max-w-full truncate text-left md:inline md:max-w-none md:overflow-visible md:whitespace-normal"
                          title={`View orders by ${user.username}`}
                        >
                          <UserName name={user.username} isActive={user.is_active} />
                        </button>
                      </td>
                      <td className="px-2 sm:px-4 py-3 text-right text-gray-600">{user.order_count}</td>
                      <td className="px-2 sm:px-4 py-3 text-right font-medium text-gray-800 whitespace-nowrap">{formatMoney(user.total_spend)}</td>
                    </tr>
                  ))}
                  {sortedUsers.length === 0 && (
                    <tr>
                      <td colSpan="3" className="px-4 py-6 text-center text-gray-500">No orders yet</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </CollapsibleSection>
        )}
      </div>
    </div>
  );
}

export default AdminDashboard;
